import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import { api } from "../../services/api";
import {
  PlayArrow,
  CheckCircle,
  Cancel,
  AccessTime,
  Refresh,
  CloudUpload,
} from "@mui/icons-material";
import LambdaTestModal from "./LambdaTestModal";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const TestCasesSection = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const RunningTestsSection = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  max-height: 600px;
  overflow-y: auto;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.2);
  `}
`;

const TestTypeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const TestTypeLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};

  &:first-child {
    margin-top: 0;
  }
`;

const TestCaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const TestCaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TestCaseName = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const RunningTestCard = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surfaceElevated};
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const RunningTestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RunningTestName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  font-size: 12px;
  font-weight: 500;

  ${(props) => {
    const colors = {
      pending: {
        bg: "#fef3c7",
        text: "#92400e",
        border: "#fbbf24",
      },
      running: {
        bg: "#dbeafe",
        text: "#1e40af",
        border: "#3b82f6",
      },
      completed: {
        bg: "#d1fae5",
        text: "#065f46",
        border: "#10b981",
      },
      failed: {
        bg: "#fee2e2",
        text: "#991b1b",
        border: "#ef4444",
      },
      cancelled: {
        bg: "#f3f4f6",
        text: "#374151",
        border: "#9ca3af",
      },
    };
    const color = colors[props.status] || colors.pending;
    return `
      background-color: ${color.bg};
      color: ${color.text};
      border: 1px solid ${color.border};
    `;
  }}
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  transition: width 0.3s ease;
  ${(props) =>
    props.theme?.isDark &&
    `
    box-shadow: 0 0 10px rgba(25, 184, 136, 0.5);
  `}
`;

const LogPreview = styled.div`
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  font-family: monospace;
  max-height: 60px;
  overflow-y: auto;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  padding-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border-top: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const PaginationInfo = styled.div`
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  font-size: 14px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const TestRunsList = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { isDark };

  const [testRuns, setTestRuns] = useState([]);
  const [runningTests, setRunningTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [lambdaTestModalOpen, setLambdaTestModalOpen] =
    useState(false);
  const [lambdaTestModalType, setLambdaTestModalType] =
    useState(null);
  const [
    lambdaTestModalTestName,
    setLambdaTestModalTestName,
  ] = useState(null);

  // Test case-lərin siyahısı
  const testCases = {
    unit: [
      {
        name: "Unit Tests",
        type: "unit",
        description: "Bütün unit testləri işə sal",
      },
    ],
    integration: [
      {
        name: "Integration Tests",
        type: "integration",
        description: "Bütün integration testləri işə sal",
      },
      {
        name: "API Tests",
        type: "integration",
        description: "API integration testləri",
      },
    ],
    e2e: [
      {
        name: "Registration Flow",
        type: "e2e",
        description: "Qeydiyyat axını testi (LambdaTest)",
        isLambdaTest: true,
        testName: "registration-flow",
      },
      {
        name: "Daily Tracking Flow",
        type: "e2e",
        description:
          "Gündəlik izləmə axını testi (LambdaTest)",
        isLambdaTest: true,
        testName: "daily-tracking-flow",
      },
    ],
    all: [
      {
        name: "All Tests",
        type: "all",
        description: "Bütün testləri işə sal",
      },
    ],
    lambdatest: [
      {
        name: "LambdaTest",
        type: "lambdatest",
        description:
          "LambdaTest-də real device testləri işə sal",
        isLambdaTest: true,
      },
    ],
  };

  const fetchTestRuns = useCallback(
    async (showLoading = false, currentPage = null) => {
      const pageToUse =
        currentPage !== null ? currentPage : page;
      if (showLoading) {
        setLoading(true);
      }
      try {
        const response = await api.getTestRuns({
          page: pageToUse,
          limit: limit,
        });

        const runs =
          response.results ||
          response.docs ||
          response.data ||
          [];
        setTestRuns(runs);
        setTotal(
          response.totalResults ||
            response.total ||
            runs.length
        );

        // Running testləri filtrlə - bütün səhifələrdən
        // Running testləri üçün ayrı query lazımdır, amma indilik bütün testləri yoxlayırıq
        const running = runs.filter(
          (run) =>
            run.status === "running" ||
            run.status === "pending"
        );
        setRunningTests(running);

        // Running testləri üçün ayrı fetch (bütün səhifələrdən)
        try {
          const runningResponse = await api.getTestRuns({
            page: 1,
            limit: 100,
          });
          const allRuns =
            runningResponse.results ||
            runningResponse.docs ||
            runningResponse.data ||
            [];
          const allRunning = allRuns.filter(
            (run) =>
              run.status === "running" ||
              run.status === "pending"
          );
          setRunningTests(allRunning);
        } catch (err) {
          console.error(
            "Error fetching running tests:",
            err
          );
        }
      } catch (error) {
        console.error("Error fetching test runs:", error);
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [page, limit]
  );

  useEffect(() => {
    fetchTestRuns(true, page);

    // Canlı yeniləmə üçün interval (yalnız running testləri yenilə)
    const interval = setInterval(() => {
      fetchTestRuns(false, page);
    }, 3000); // Hər 3 saniyədə bir yenilə

    return () => {
      clearInterval(interval);
    };
  }, [fetchTestRuns, page]);

  const handleRunTest = async (data) => {
    if (typeof data === "string") {
      // Legacy format - just type
      if (
        window.confirm(
          `${data} testləri işə salmaq istəyirsiniz?`
        )
      ) {
        setCreating(true);
        try {
          await api.runTests({ type: data });
          setTimeout(() => {
            fetchTestRuns(false);
          }, 1000);
        } catch (error) {
          console.error("Error running tests:", error);
          alert(
            error.response?.data?.message ||
              error.message ||
              "Xəta baş verdi"
          );
        } finally {
          setCreating(false);
        }
      }
    } else {
      // New format - full config object
      setCreating(true);
      try {
        await api.runTests(data);
        setTimeout(() => {
          fetchTestRuns(false);
        }, 1000);
      } catch (error) {
        console.error("Error running tests:", error);
        alert(
          error.response?.data?.message ||
            error.message ||
            "Xəta baş verdi"
        );
      } finally {
        setCreating(false);
      }
    }
  };

  const handleImportTestCases = async () => {
    if (
      window.confirm(
        "TEST_CASES.md faylından bütün test case-ləri import etmək istəyirsiniz?"
      )
    ) {
      setImporting(true);
      try {
        const result = await api.importTestCases();
        alert(
          `✅ Import tamamlandı!\n` +
            `- ${result.created} yeni test case yaradıldı\n` +
            `- ${result.updated} test case yeniləndi\n` +
            `- Ümumi: ${result.total} test case`
        );
        // List-i yenilə
        fetchTestRuns(false);
      } catch (error) {
        console.error("Error importing test cases:", error);
        alert(
          error.response?.data?.message ||
            error.message ||
            "Xəta baş verdi"
        );
      } finally {
        setImporting(false);
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle fontSize="small" />;
      case "failed":
        return <Cancel fontSize="small" />;
      case "running":
        return <AccessTime fontSize="small" />;
      default:
        return <AccessTime fontSize="small" />;
    }
  };

  const getLatestLog = (logs) => {
    if (!logs || logs.length === 0) return "Başladı...";
    return logs[logs.length - 1]?.message || "İşləyir...";
  };

  const getProgress = (testRun) => {
    if (!testRun.results) return 0;
    const { total, passed, failed } = testRun.results;
    if (total === 0) return 0;
    return Math.round(((passed + failed) / total) * 100);
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>Test Runs</Title>
          <div
            style={{
              display: "flex",
              gap: getTheme(isDark).spacing.sm,
            }}
          >
            <Button
              onClick={handleImportTestCases}
              variant="primary"
              theme={theme}
              disabled={importing}
            >
              <CloudUpload /> Test Case-ləri Import Et
            </Button>
            <Button
              onClick={() => fetchTestRuns()}
              variant="outline"
              theme={theme}
              disabled={loading}
            >
              <Refresh /> Yenilə
            </Button>
          </div>
        </PageHeader>

        <ContentGrid theme={theme}>
          <div>
            <TestCasesSection theme={theme}>
              <SectionTitle theme={theme}>
                Test Case-lər
              </SectionTitle>

              {Object.entries(testCases).map(
                ([category, cases]) => (
                  <TestTypeGroup
                    key={category}
                    theme={theme}
                  >
                    <TestTypeLabel theme={theme}>
                      {category === "unit" &&
                        "🔬 Unit Tests"}
                      {category === "integration" &&
                        "🔗 Integration Tests"}
                      {category === "e2e" && "🎯 E2E Tests"}
                      {category === "all" && "🚀 All Tests"}
                      {category === "lambdatest" &&
                        "☁️ LambdaTest"}
                    </TestTypeLabel>
                    <TestCaseList theme={theme}>
                      {cases.map((testCase, index) => (
                        <TestCaseButton
                          key={index}
                          theme={theme}
                          onClick={() => {
                            if (testCase.isLambdaTest) {
                              setLambdaTestModalType(
                                testCase.type
                              );
                              setLambdaTestModalTestName(
                                testCase.testName || null
                              );
                              setLambdaTestModalOpen(true);
                            } else {
                              handleRunTest(testCase.type);
                            }
                          }}
                          disabled={creating}
                        >
                          <div>
                            <TestCaseName theme={theme}>
                              {testCase.name}
                            </TestCaseName>
                            {testCase.description && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color:
                                    getTheme(isDark).colors
                                      .textSecondary,
                                  marginTop: "4px",
                                }}
                              >
                                {testCase.description}
                              </div>
                            )}
                          </div>
                          <PlayArrow />
                        </TestCaseButton>
                      ))}
                    </TestCaseList>
                  </TestTypeGroup>
                )
              )}
            </TestCasesSection>

            {/* Test Runs Table */}
            <Card
              theme={theme}
              style={{
                marginTop: getTheme(isDark).spacing.lg,
              }}
            >
              <SectionTitle theme={theme}>
                Test Runs Siyahısı
              </SectionTitle>
              {loading ? (
                <EmptyState theme={theme}>
                  Yüklənir...
                </EmptyState>
              ) : testRuns.length === 0 ? (
                <EmptyState theme={theme}>
                  Test run tapılmadı
                </EmptyState>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: getTheme(isDark).spacing.sm,
                    }}
                  >
                    {testRuns.map((run) => (
                      <TestCaseButton
                        key={run.id || run._id}
                        theme={theme}
                        onClick={() =>
                          navigate(
                            `/admin/test-runs/${
                              run.id || run._id
                            }`
                          )
                        }
                      >
                        <div>
                          <TestCaseName theme={theme}>
                            {run.name}
                          </TestCaseName>
                          <div
                            style={{
                              fontSize: "12px",
                              color:
                                getTheme(isDark).colors
                                  .textSecondary,
                              marginTop: "4px",
                            }}
                          >
                            {new Date(
                              run.createdAt
                            ).toLocaleString("az-AZ")}
                          </div>
                        </div>
                        <StatusBadge
                          status={run.status}
                          theme={theme}
                        >
                          {getStatusIcon(run.status)}
                          {run.status}
                        </StatusBadge>
                      </TestCaseButton>
                    ))}
                  </div>

                  {/* Pagination */}
                  {Math.ceil(total / limit) > 1 && (
                    <PaginationContainer theme={theme}>
                      <PaginationInfo theme={theme}>
                        {(page - 1) * limit + 1}-
                        {Math.min(page * limit, total)} /{" "}
                        {total} test run
                      </PaginationInfo>
                      <PaginationButtons theme={theme}>
                        <Button
                          onClick={() => {
                            const newPage = Math.max(
                              1,
                              page - 1
                            );
                            setPage(newPage);
                            fetchTestRuns(true, newPage);
                          }}
                          disabled={page === 1}
                          variant="outline"
                          theme={theme}
                        >
                          Əvvəlki
                        </Button>
                        <Button
                          onClick={() => {
                            const newPage = Math.min(
                              Math.ceil(total / limit),
                              page + 1
                            );
                            setPage(newPage);
                            fetchTestRuns(true, newPage);
                          }}
                          disabled={
                            page >= Math.ceil(total / limit)
                          }
                          variant="outline"
                          theme={theme}
                        >
                          Növbəti
                        </Button>
                      </PaginationButtons>
                    </PaginationContainer>
                  )}
                </>
              )}
            </Card>
          </div>

          {/* Running Tests Sidebar */}
          <RunningTestsSection theme={theme}>
            <SectionTitle theme={theme}>
              Canlı Testlər
            </SectionTitle>
            {runningTests.length === 0 ? (
              <EmptyState theme={theme}>
                Hal-hazırda işləyən test yoxdur
              </EmptyState>
            ) : (
              runningTests.map((testRun) => (
                <RunningTestCard
                  key={testRun.id || testRun._id}
                  theme={theme}
                >
                  <RunningTestHeader>
                    <RunningTestName theme={theme}>
                      {testRun.name}
                    </RunningTestName>
                    <StatusBadge
                      status={testRun.status}
                      theme={theme}
                    >
                      {getStatusIcon(testRun.status)}
                      {testRun.status}
                    </StatusBadge>
                  </RunningTestHeader>

                  {testRun.status === "running" &&
                    testRun.results && (
                      <>
                        <ProgressBar theme={theme}>
                          <ProgressFill
                            style={{
                              width: `${getProgress(
                                testRun
                              )}%`,
                            }}
                            theme={theme}
                          />
                        </ProgressBar>
                        <div
                          style={{
                            fontSize: "12px",
                            color:
                              getTheme(isDark).colors
                                .textSecondary,
                          }}
                        >
                          {testRun.results.passed || 0}{" "}
                          passed,{" "}
                          {testRun.results.failed || 0}{" "}
                          failed /{" "}
                          {testRun.results.total || 0} total
                        </div>
                      </>
                    )}

                  <LogPreview theme={theme}>
                    {getLatestLog(testRun.logs)}
                  </LogPreview>

                  <Button
                    onClick={() =>
                      navigate(
                        `/admin/test-runs/${
                          testRun.id || testRun._id
                        }`
                      )
                    }
                    variant="outline"
                    theme={theme}
                    style={{
                      marginTop:
                        getTheme(isDark).spacing.xs,
                    }}
                  >
                    Detalları gör
                  </Button>
                </RunningTestCard>
              ))
            )}
          </RunningTestsSection>
        </ContentGrid>

        <LambdaTestModal
          isOpen={lambdaTestModalOpen}
          onClose={() => {
            setLambdaTestModalOpen(false);
            setLambdaTestModalType(null);
            setLambdaTestModalTestName(null);
          }}
          onRun={handleRunTest}
          testType={lambdaTestModalType}
          testName={lambdaTestModalTestName}
        />
      </PageContainer>
    </MainLayout>
  );
};

export default TestRunsList;
